import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table,
    Badge, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { API_BASE_URL } from "../constants/api";
import { ROLES, ROLE_LABELS, ROLE_COLORS, MODULES, ROLE_PERMISSIONS } from "../config/permissions";

export default function UtilisateursRoles() {
    const navigate = useNavigate();
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentUserRole, setCurrentUserRole] = useState(null);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        fetchRoles();
        fetchStats();
        getCurrentUserRole();
    }, []);

    const getCurrentUserRole = () => {
        try {
            const userStr = localStorage.getItem("user");
            if (userStr) {
                const user = JSON.parse(userStr);
                setCurrentUserRole(user.role);
            }
        } catch (error) {
            console.error("Erreur récupération rôle:", error);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=stats_utilisateurs`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement stats", err);
        }
    };

    const fetchRoles = async () => {
        try {
            setLoading(true);
            // Créer la liste des rôles à partir de ROLES
            const rolesList = Object.keys(ROLES).map(key => ({
                id: ROLES[key],
                label: ROLE_LABELS[ROLES[key]],
                description: getRoleDescription(ROLES[key]),
                usersCount: stats?.par_role?.[ROLES[key]] || 0,
                permissions: ROLE_PERMISSIONS[ROLES[key]] || {}
            }));
            setRoles(rolesList);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getRoleDescription = (role) => {
        const descriptions = {
            [ROLES.ADMIN]: 'Accès complet à toutes les fonctionnalités du système',
            [ROLES.GESTIONNAIRE]: 'Gestion opérationnelle complète (sauf utilisateurs)',
            [ROLES.COMMERCIAL]: 'Focus sur les ventes et la relation client',
            [ROLES.MAGASINIER]: 'Gestion du stock et des inventaires'
        };
        return descriptions[role] || '';
    };

    const modulesList = MODULES ? Object.values(MODULES).map(moduleId => {
        const modulePermissions = {
            [MODULES.DASHBOARD]: ['view'],
            [MODULES.CLIENTS]: ['view', 'create', 'edit', 'delete', 'import'],
            [MODULES.FOURNISSEURS]: ['view', 'create', 'edit', 'delete'],
            [MODULES.PRODUITS]: ['view', 'create', 'edit', 'delete', 'import'],
            [MODULES.STOCK]: ['view', 'edit', 'inventory', 'traceability'],
            [MODULES.VENTES]: ['view', 'create', 'edit', 'delete', 'invoice', 'proforma', 'bl', 'payment'],
            [MODULES.ACHATS]: ['view', 'create', 'edit', 'delete', 'order', 'reception'],
            [MODULES.STATISTIQUES]: ['view', 'export'],
            [MODULES.PARAMETRES]: ['view', 'edit'],
            [MODULES.UTILISATEURS]: ['view', 'create', 'edit', 'delete'],
            [MODULES.SCANNER]: ['view', 'scan']
        };

        const moduleLabels = {
            [MODULES.DASHBOARD]: 'Tableau de bord',
            [MODULES.CLIENTS]: 'Clients',
            [MODULES.FOURNISSEURS]: 'Fournisseurs',
            [MODULES.PRODUITS]: 'Produits',
            [MODULES.STOCK]: 'Stock',
            [MODULES.VENTES]: 'Ventes',
            [MODULES.ACHATS]: 'Achats',
            [MODULES.STATISTIQUES]: 'Statistiques',
            [MODULES.PARAMETRES]: 'Paramètres',
            [MODULES.UTILISATEURS]: 'Utilisateurs',
            [MODULES.SCANNER]: 'Scanner'
        };

        const moduleIcons = {
            [MODULES.DASHBOARD]: 'ri-dashboard-line',
            [MODULES.CLIENTS]: 'ri-user-star-line',
            [MODULES.FOURNISSEURS]: 'ri-truck-line',
            [MODULES.PRODUITS]: 'ri-shopping-bag-line',
            [MODULES.STOCK]: 'ri-stack-line',
            [MODULES.VENTES]: 'ri-shopping-cart-line',
            [MODULES.ACHATS]: 'ri-exchange-dollar-line',
            [MODULES.STATISTIQUES]: 'ri-bar-chart-line',
            [MODULES.PARAMETRES]: 'ri-settings-line',
            [MODULES.UTILISATEURS]: 'ri-user-settings-line',
            [MODULES.SCANNER]: 'ri-qr-code-line'
        };

        return {
            id: moduleId,
            label: moduleLabels[moduleId] || moduleId,
            icon: moduleIcons[moduleId] || 'ri-question-line',
            permissions: modulePermissions[moduleId] || ['view']
        };
    }) : [];

    const getPermissionIcon = (roleId, moduleId, permission) => {
        if (!roleId || !moduleId || !permission) {
            return (
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Données manquantes</Tooltip>}
                >
                    <i className="ri-question-line text-warning fs-5" style={{ cursor: 'help' }}></i>
                </OverlayTrigger>
            );
        }

        const hasPermission = ROLE_PERMISSIONS?.[roleId]?.[moduleId]?.includes(permission) || false;
        
        return hasPermission ? (
            <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Accès autorisé</Tooltip>}
            >
                <i className="ri-checkbox-circle-fill text-success fs-5" style={{ cursor: 'help' }}></i>
            </OverlayTrigger>
        ) : (
            <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Accès restreint</Tooltip>}
            >
                <i className="ri-close-circle-fill text-danger fs-5" style={{ cursor: 'help', opacity: 0.5 }}></i>
            </OverlayTrigger>
        );
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des rôles et permissions...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-shield-user-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Rôles & permissions</h4>
                                    <p className="text-muted small mb-0">Matrice des droits d'accès par rôle</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/parametres')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {error && (
                        <Alert variant="danger" className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {error}
                        </Alert>
                    )}

                    {/* Cartes récapitulatives des rôles */}
                    <Row className="g-4 mb-4">
                        {roles.map(role => (
                            <Col key={role.id} md={6} lg={3}>
                                <div className="role-card">
                                    <div className="role-card-header bg-info" style={{ borderBottom: `3px solid ${ROLE_COLORS[role.id]}` }}>
                                        <span className="role-badge" style={{ background: ROLE_COLORS[role.id] }}>
                                            {role.label}
                                        </span>
                                        <span className="role-count text-white">{role.usersCount} utilisateur(s)</span>
                                    </div>
                                    <div className="role-card-body">
                                        <p className="role-description">{role.description}</p>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>

                    {/* Tableau des permissions */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                            <div className="d-flex align-items-center gap-2">
                                <div className="section-icon-small">
                                    <i className="ri-shield-keyhole-line text-primary"></i>
                                </div>
                                <h6 className="mb-0 fw-semibold">Matrice des permissions</h6>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 permissions-table">
                                    <thead className="bg-info">
                                        <tr>
                                            <th className="ps-4" style={{ width: '250px' }}>Module</th>
                                            {roles.map(role => (
                                                <th key={role.id} className="text-center">
                                                    <span className={`role-header-badge ${ROLE_COLORS[role.id]}`} >
                                                        {role.label}
                                                    </span>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {modulesList.length > 0 ? (
                                            modulesList.map(module => (
                                                <tr key={module.id}>
                                                    <td className="ps-4">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="module-icon">
                                                                <i className={module.icon}></i>
                                                            </div>
                                                            <div>
                                                                <span className="fw-semibold">{module.label}</span>
                                                                <div className="permissions-badges">
                                                                    {module.permissions.map(p => (
                                                                        <span key={p} className="permission-badge">
                                                                            {p}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    {roles.map(role => (
                                                        <td key={role.id} className="text-center">
                                                            <div className="d-flex justify-content-center gap-1 flex-wrap">
                                                                {module.permissions.map(permission => (
                                                                    <span key={permission}>
                                                                        {getPermissionIcon(role.id, module.id, permission)}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={roles.length + 1} className="text-center py-5">
                                                    <i className="ri-error-warning-line fs-48 text-muted mb-3 d-block"></i>
                                                    <h5>Aucun module trouvé</h5>
                                                    <p className="text-muted">Vérifiez la configuration des permissions</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                        <Card.Footer className="bg-transparent border-0 pt-0 pb-4">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                <div className="d-flex align-items-center gap-2 text-muted small">
                                    <i className="ri-information-line fs-5 text-info"></i>
                                    <span>Les permissions sont prédéfinies selon les rôles standards.</span>
                                </div>
                                <div className="d-flex gap-4">
                                    <div className="d-flex align-items-center gap-1">
                                        <i className="ri-checkbox-circle-fill text-success fs-5"></i>
                                        <small>Accès autorisé</small>
                                    </div>
                                    <div className="d-flex align-items-center gap-1">
                                        <i className="ri-close-circle-fill text-danger fs-5 opacity-50"></i>
                                        <small>Accès restreint</small>
                                    </div>
                                </div>
                            </div>
                        </Card.Footer>
                    </Card>

                    {/* Note sur les permissions */}
                    {currentUserRole && currentUserRole !== ROLES.ADMIN && (
                        <Alert variant="info" className="mt-4 alert-modern">
                            <i className="ri-information-line me-2 fs-5"></i>
                            Vous êtes connecté en tant que <strong>{ROLE_LABELS[currentUserRole]}</strong>.
                            Certaines options peuvent être limitées selon vos permissions.
                        </Alert>
                    )}
                </div>
                <Footer />
            </div>

            <style>{`
                /* Role Cards */
                .role-card {
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    overflow: hidden;
                    height: 100%;
                }
                .role-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .role-card-header {
                    padding: 1rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    background: #f8fafc;
                }
                .role-badge {
                    padding: 0.3rem 1rem;
                    border-radius: 20px;
                    font-size: 0.8rem;
                    font-weight: 600;
                    color: white;
                }
                .role-count {
                    font-size: 0.7rem;
                    color: #64748b;
                }
                .role-card-body {
                    padding: 1rem;
                }
                .role-description {
                    font-size: 0.8rem;
                    color: #475569;
                    margin: 0;
                }
                
                /* Section Icon Small */
                .section-icon-small {
                    width: 32px;
                    height: 32px;
                    background: linear-gradient(135deg, #21ACCD20, #1a8ba820);
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                /* Module Icon */
                .module-icon {
                    width: 32px;
                    height: 32px;
                    background: #e2f3f5;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #21ACCD;
                }
                
                /* Permissions Badges */
                .permissions-badges {
                    margin-top: 4px;
                }
                .permission-badge {
                    display: inline-block;
                    background: #f1f5f9;
                    padding: 0.1rem 0.4rem;
                    border-radius: 4px;
                    font-size: 0.6rem;
                    font-weight: 500;
                    text-transform: uppercase;
                    color: #475569;
                    margin-right: 4px;
                }
                
                .role-header-badge {
                    display: inline-block;
                    padding: 0.3rem 1rem;
                    border-radius: 20px;
                    font-size: 0.7rem;
                    font-weight: 600;
                }
                
                /* Permissions Table */
                .permissions-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .permissions-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .permissions-table tbody tr:hover {
                    background: #f8fafc;
                }
                
                /* Alert Modern */
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </>
    );
}